/* eslint-disable react-hooks/refs -- intentional render-time ref read
 * + write: this hook is opt-in diagnostic instrumentation gated by
 * RENDER_DEBUG. The counter MUST increment on every render call so
 * the logged number tracks actual commits. The prev-props ref is
 * compared identity-wise to detect which props caused this render. */
import { useRef } from "react";
import { RENDER_DEBUG } from "@/lib/debug-flags";

/**
 * Render-counter + prop-diff diagnostic. Logs which props changed to
 * cause this render whenever `RENDER_DEBUG` is enabled. Free of
 * observable side effects when the flag is off (still maintains the
 * counter ref + diffs the props bag, but skips the console call).
 *
 * Call once at the top of the component, passing a snapshot of the
 * current props bag. The hook keeps the prior props in a ref and
 * compares by identity.
 */
export function useRenderDiagnostics(
  componentName: string,
  contextSummary: string,
  currentProps: Record<string, unknown>,
) {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  const prevPropsRef = useRef<Record<string, unknown>>({});

  const changedProps: string[] = [];
  for (const key of Object.keys(currentProps)) {
    if (prevPropsRef.current[key] !== currentProps[key]) {
      changedProps.push(key);
    }
  }
  prevPropsRef.current = currentProps;

  if (RENDER_DEBUG)
    console.debug(
      `[${componentName}] render #${renderCountRef.current}`,
      contextSummary,
      changedProps.length > 0
        ? `CHANGED: ${changedProps.join(", ")}`
        : "INTERNAL (no prop change)",
    );
}
